import React, { useState, useEffect } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Activity, TrendingUp, User, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { axiosInstance } from '../App';

const UtilizationReport = ({ resourceId, resourceName }) => {
  const [loading, setLoading] = useState(true);
  const [utilizationData, setUtilizationData] = useState(null);
  const [dateRange, setDateRange] = useState({
    start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    if (resourceId) {
      fetchUtilization();
    }
  }, [resourceId, dateRange]);

  const fetchUtilization = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(dateRange);
      const response = await axiosInstance.get(
        `/reports/utilization/person/${resourceId}?${params.toString()}`
      );
      setUtilizationData(response.data);
    } catch (error) {
      toast.error('Kullanım raporu yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!utilizationData) {
    return (
      <div className="text-center py-12 text-gray-600">
        Kullanım verisi bulunamadı
      </div>
    );
  }

  const {
    resource_name,
    capacity_per_week,
    weekly_utilization
  } = utilizationData;

  // Calculate averages
  const avgUtilization = weekly_utilization.length > 0
    ? weekly_utilization.reduce((sum, w) => sum + w.utilization_percent, 0) / weekly_utilization.length
    : 0;

  const avgAllocatedHours = weekly_utilization.length > 0
    ? weekly_utilization.reduce((sum, w) => sum + w.allocated_hours, 0) / weekly_utilization.length
    : 0;

  const getUtilizationStatus = (percent) => {
    if (percent < 70) return { color: 'text-yellow-600', bg: 'bg-yellow-100', label: 'Düşük' };
    if (percent > 85) return { color: 'text-red-600', bg: 'bg-red-100', label: 'Yüksek' };
    return { color: 'text-green-600', bg: 'bg-green-100', label: 'İdeal' };
  };

  const status = getUtilizationStatus(avgUtilization);

  return (
    <div className="space-y-6">
      {/* Date Range Filter */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Başlangıç
            </label>
            <input
              type="date"
              value={dateRange.start_date}
              onChange={(e) => setDateRange({...dateRange, start_date: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bitiş
            </label>
            <input
              type="date"
              value={dateRange.end_date}
              onChange={(e) => setDateRange({...dateRange, end_date: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Resource Name */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Kaynak</span>
            <User size={20} className="text-blue-600" />
          </div>
          <div className="text-lg font-bold text-gray-900">
            {resource_name}
          </div>
        </div>

        {/* Capacity */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Haftalık Kapasite</span>
            <Calendar size={20} className="text-purple-600" />
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {capacity_per_week}h
          </div>
        </div>

        {/* Avg Utilization */}
        <div className={`rounded-lg shadow-sm p-6 ${status.bg}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Ort. Kullanım</span>
            <Activity size={20} className={status.color} />
          </div>
          <div className={`text-2xl font-bold ${status.color}`}>
            {avgUtilization.toFixed(1)}%
          </div>
          <div className="text-xs text-gray-600 mt-1">
            {status.label}
          </div>
        </div>

        {/* Avg Hours */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Ort. Saat/Hafta</span>
            <TrendingUp size={20} className="text-green-600" />
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {avgAllocatedHours.toFixed(1)}h
          </div>
        </div>
      </div>

      {/* Status Message */}
      <div className={`rounded-lg p-4 ${status.bg}`}>
        <div className="flex items-center gap-2">
          <Activity size={20} className={status.color} />
          <span className={`font-medium ${status.color}`}>
            {avgUtilization < 70 && 'Düşük Kullanım: Daha fazla görev atanabilir'}
            {avgUtilization >= 70 && avgUtilization <= 85 && 'İdeal Kullanım Oranı'}
            {avgUtilization > 85 && 'Yüksek Kullanım: Aşırı yüklenme riski!'}
          </span>
        </div>
        <p className="text-sm mt-1">
          Hedef aralık: %70-%85 | Mevcut: %{avgUtilization.toFixed(1)}
        </p>
      </div>

      {/* Weekly Utilization Chart */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Haftalık Kullanım Trendi</h3>
        <ResponsiveContainer width="100%" height={350}>
          <BarChart data={weekly_utilization}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="week" 
              tick={{ fontSize: 12 }}
            />
            <YAxis 
              tick={{ fontSize: 12 }}
              label={{ value: 'Saat', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip 
              contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb' }}
            />
            <Legend />
            <Bar 
              dataKey="allocated_hours" 
              fill="#3B82F6" 
              name="Atanan Saat"
            />
            <Bar 
              dataKey="capacity" 
              fill="#10B981" 
              name="Kapasite"
              fillOpacity={0.3}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Utilization Percentage Chart */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Kullanım Yüzdesi</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={weekly_utilization}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="week" 
              tick={{ fontSize: 12 }}
            />
            <YAxis 
              tick={{ fontSize: 12 }}
              domain={[0, 100]}
              label={{ value: '%', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip 
              formatter={(value) => `${value.toFixed(1)}%`}
              contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb' }}
            />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="utilization_percent" 
              stroke="#3B82F6" 
              strokeWidth={2}
              name="Kullanım %"
              dot={{ fill: '#3B82F6', r: 4 }}
            />
            {/* Target lines */}
            <Line 
              type="monotone" 
              dataKey={() => 70} 
              stroke="#10B981" 
              strokeDasharray="5 5"
              name="Min Hedef (70%)"
              dot={false}
            />
            <Line 
              type="monotone" 
              dataKey={() => 85} 
              stroke="#EF4444" 
              strokeDasharray="5 5"
              name="Max Hedef (85%)"
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Weekly Details Table */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Haftalık Detaylar</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 px-4 text-sm font-medium text-gray-600">Hafta</th>
                <th className="text-right py-2 px-4 text-sm font-medium text-gray-600">Atanan Saat</th>
                <th className="text-right py-2 px-4 text-sm font-medium text-gray-600">Kapasite</th>
                <th className="text-right py-2 px-4 text-sm font-medium text-gray-600">Kullanım %</th>
                <th className="text-center py-2 px-4 text-sm font-medium text-gray-600">Durum</th>
              </tr>
            </thead>
            <tbody>
              {weekly_utilization.map((week, idx) => {
                const weekStatus = getUtilizationStatus(week.utilization_percent);
                return (
                  <tr key={idx} className="border-b hover:bg-gray-50">
                    <td className="py-2 px-4 text-sm">{week.week}</td>
                    <td className="py-2 px-4 text-sm text-right">{week.allocated_hours}h</td>
                    <td className="py-2 px-4 text-sm text-right">{week.capacity}h</td>
                    <td className="py-2 px-4 text-sm text-right font-medium">{week.utilization_percent.toFixed(1)}%</td>
                    <td className="py-2 px-4 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs ${weekStatus.bg} ${weekStatus.color}`}>
                        {weekStatus.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UtilizationReport;
